import Enum from '@/modules/core/app/helpers/Enum';
import Lang from '@/modules/core/app/services/Lang';

export class AlternateType extends Enum {
  key!: AlternateTypeKey;
  value!: string;
  description!: string;
  getValue?: (name: string) => string;
  getDescription?: (name: string) => string;

  static TO_THEIR_DESCENDANTS: AlternateType = {
    key: 'to_their_descendants',
    get value(): string {
      return Lang.t('estatedistribution.to-their-descendants');
    },
    getValue: (name: string) => {
      return Lang.t('vaultdistributionsetting.names-descendants', { name });
    },
    get description(): string {
      return Lang.t('estatedistribution.beneficiary-passed-children');
    },
    getDescription: (name: string) => {
      return Lang.t('vaultdistributionsetting.if-name-their-share', { name });
    }
  };

  static EQUAL_SPLIT_TO_MY_GRANDCHILDREN: AlternateType = {
    key: 'equal_split_to_my_existing_and_future_children',
    get value(): string {
      return Lang.t('vaultdistributionsetting.my-existing-future-grandchildren');
    },
    getValue: (name: string) => {
      return Lang.t('vaultdistributionsetting.names-existing-future-children', { name });
    },
    get description(): string {
      return Lang.t('vaultdistributionsetting.predeceased-childs-children-issue');
    }
  };

  static EQUAL_SPLIT_TO_THEIR_CHILDREN: AlternateType = {
    key: 'equal_split_to_their_existing_and_future_children',
    get value(): string {
      return Lang.t('vaultdistributionsetting.beneficiarys-existing-future-children');
    },
    get description(): string {
      return Lang.t('vaultdistributionsetting.predeceased-beneficiarys-children-issue');
    }
  };

  // this is different from `to_the_residual` because it's used for residual distributions already
  static SURVIVING_BENEFICIARIES: AlternateType = {
    key: 'equal_split_to_other_beneficiaries',
    get value(): string {
      return Lang.t('vaultdistributionsetting.the-surviving-beneficiaries');
    },
    get description(): string {
      return Lang.t('vaultdistributionsetting.predeceased-beneficiarys-equally-residual');
    }
  };

  static EQUAL_SPLIT_TO_THEIR_SIBLINGS: AlternateType = {
    key: 'equal_split_to_their_siblings',
    get value(): string {
      return Lang.t('vaultdistributionsetting.my-surviving-children');
    },
    get description(): string {
      return Lang.t('vaultdistributionsetting.predeceased-childs-share-siblings');
    },
    getDescription: (name: string) => {
      return Lang.t('vaultdistributionsetting.names-share-siblings', { name });
    }
  };

  static TO_THE_RESIDUAL: AlternateType = {
    key: 'to_the_residual',
    get value(): string {
      return Lang.t('estatedistribution.to-the-residue');
    },
    get description(): string {
      return Lang.t('estatedistribution.beneficiary-distributed-residual');
    },
    getDescription: (name: string) => {
      return Lang.t('vaultdistributionsetting.if-name-residue', { name });
    }
  };

  static SOMEONE_ELSE: AlternateType = {
    key: 'someone_else',
    get value(): string {
      return Lang.t('estatedistribution.someone-else');
    },
    get description(): string {
      return Lang.t('estatedistribution.beneficiary-distributed-individual');
    },
    getDescription: (name: string) => {
      return Lang.t('vaultdistributionsetting.passed-names-share-individual', { name });
    }
  };

  static SOMEONE_ELSE_CHILD: AlternateType = {
    key: 'someone_else',
    get value(): string {
      return Lang.t('estatedistribution.someone-else');
    },
    get description(): string {
      return Lang.t('estatedistribution.predeceased-childs-share-equally');
    },
    getDescription: (name: string) => {
      return Lang.t('vaultdistributionsetting.names-share-individuals-below', { name });
    }
  };

  static getSpecificDistributionOptions(): AlternateType[] {
    return [
      AlternateType.TO_THEIR_DESCENDANTS,
      AlternateType.TO_THE_RESIDUAL,
      AlternateType.SOMEONE_ELSE
    ];
  }

  static getResidualChildOptions(): AlternateType[] {
    return [
      AlternateType.EQUAL_SPLIT_TO_MY_GRANDCHILDREN,
      AlternateType.EQUAL_SPLIT_TO_THEIR_SIBLINGS,
      AlternateType.SOMEONE_ELSE_CHILD
    ];
  }

  static getResidualOtherOptions(): AlternateType[] {
    return [
      AlternateType.EQUAL_SPLIT_TO_THEIR_CHILDREN,
      AlternateType.SURVIVING_BENEFICIARIES,
      AlternateType.SOMEONE_ELSE
    ];
  }

  static getSingleBeneficiaryResidualOtherOptions(): AlternateType[] {
    return [AlternateType.EQUAL_SPLIT_TO_THEIR_CHILDREN, AlternateType.SOMEONE_ELSE];
  }
}

export type AlternateTypeKey =
  | 'to_their_descendants'
  | 'to_the_residual'
  | 'someone_else'
  | 'equal_split_to_my_existing_and_future_children'
  | 'equal_split_to_their_existing_and_future_children'
  | 'equal_split_to_their_siblings'
  | 'equal_split_to_other_beneficiaries';
